// assets/reviews.js
(() => {
  /**
   * Robust scroll-to-reviews for Shopify + Judge.me
   * - Expands accordions/tabs if reviews are inside
   * - Offsets sticky headers
   * - Handles late render + layout shifts (images, font swap)
   */

  // ====== CONFIG ======
  const SELECTORS = {
    // What the user clicks
    trigger: '.js-scroll-to-reviews, .product-info__rating',

    // What we scroll to (first match wins)
    target: '#all-reviews, article.jdgm-widget.jdgm-all-reviews-widget, #judgeme_product_reviews, .jdgm-rev-widg',

    // Possible sticky headers (add your theme’s header selector if different)
    stickyHeader:
      '.shopify-section-header-sticky, header.site-header, .section-header, .sticky-header, .header-wrapper',

    // If your reviews are in tabs/accordion, list containers here
    // (Dawn-style collapsible, tab components, generic details)
    containersToOpen:
      '.product__accordion, .accordion, .collapsible-content, .tabs, .tab-panels, .product-tabs, details, [data-collapsible], [aria-controls][aria-expanded]'
  };

  const MAX_WAIT_MS = 10000;     // stop waiting after 10s
  const POST_SCROLL_RETRY_MS = 600; // re-align after layout settles
  const EXTRA_PAD = 12;          // breathing room under the header

  // ====== HELPERS ======
  function once(fn) {
    let ran = false;
    return (...args) => { if (!ran) { ran = true; fn(...args); } };
  }

  function getStickyHeaderHeight() {
    let h = 0;
    document.querySelectorAll(SELECTORS.stickyHeader).forEach(el => {
      const styles = getComputedStyle(el);
      // Only count visible, fixed/sticky headers
      const isVisible = el.offsetHeight && styles.display !== 'none' && styles.visibility !== 'hidden';
      const isFixedOrSticky = /(fixed|sticky)/.test(styles.position);
      if (isVisible && isFixedOrSticky) {
        h += el.getBoundingClientRect().height;
      }
    });
    return h;
  }

  function findTarget() {
    return document.querySelector(SELECTORS.target);
  }

  function isHidden(el) {
    if (!el) return true;
    const rect = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    return (
      !el.offsetParent ||
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      rect.width === 0 && rect.height === 0
    );
  }

  // Open common container patterns that might be hiding the reviews
  function revealContainersFor(el) {
    if (!el) return;

    // Expand <details>
    let node = el;
    for (let i = 0; i < 6 && node; i++, node = node.parentElement) {
      if (node.tagName === 'DETAILS' && !node.open) node.open = true;
    }

    // Click any aria-controls toggles that hide the target
    const toggles = Array.from(document.querySelectorAll(SELECTORS.containersToOpen));
    toggles.forEach(tg => {
      // Heuristics: if the target is inside tg but tg looks collapsed, expand it
      // Dawn collapsible content
      if (tg.hasAttribute('aria-expanded') && tg.getAttribute('aria-expanded') === 'false') {
        tg.click();
      }
      // Generic .is-collapsed / .hidden classes
      const st = getComputedStyle(tg);
      if (st.display === 'none' || st.visibility === 'hidden') {
        // Try to find a button that toggles this panel
        const btn = document.querySelector(`[aria-controls="${tg.id}"]`);
        if (btn) btn.click();
      }
    });

    // If tabs are used, activate the Reviews tab by looking for obvious labels
    const reviewsTab =
      document.querySelector('[role="tab"][aria-controls*="review" i]') ||
      Array.from(document.querySelectorAll('[role="tab"]'))
        .find(t => /review/.test((t.textContent || '').toLowerCase()));
    if (reviewsTab && reviewsTab.getAttribute('aria-selected') !== 'true') {
      reviewsTab.click();
    }
  }

  function supportsNativeSmoothScroll() {
    return 'scrollBehavior' in document.documentElement.style;
  }

  function animateScrollTo(y) {
    // JS fallback animation for browsers without smooth behavior or when prefers-reduced-motion is on
    const start = window.pageYOffset;
    const distance = y - start;
    const duration = 350; // ms
    const startTime = performance.now();

    function step(now) {
      const t = Math.min(1, (now - startTime) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      window.scrollTo(0, start + distance * eased);
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function doScrollTo(el) {
    const header = getStickyHeaderHeight();
    const y = Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - header - EXTRA_PAD);

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReduced && supportsNativeSmoothScroll()) {
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      animateScrollTo(y);
    }

    // Re-align after layout shifts (images/fonts load)
    setTimeout(() => {
      const header2 = getStickyHeaderHeight();
      const y2 = Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - header2 - EXTRA_PAD);
      window.scrollTo({ top: y2 });
    }, POST_SCROLL_RETRY_MS);
  }

  function waitForTargetAndScroll() {
    // If already there and visible
    const ready = findTarget();
    if (ready) {
      revealContainersFor(ready);
      // After reveal, if still hidden, give DOM a tick
      setTimeout(() => doScrollTo(ready), 0);
      return;
    }

    // Otherwise observe DOM for late-inserted Judge.me widget
    const stop = once(() => obs.disconnect());
    const obs = new MutationObserver(() => {
      const t = findTarget();
      if (t) {
        stop();
        revealContainersFor(t);
        setTimeout(() => doScrollTo(t), 0);
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(stop, MAX_WAIT_MS);
  }

  // Delegate clicks
  document.addEventListener('click', (e) => {
    const trg = e.target.closest(SELECTORS.trigger);
    if (!trg) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    waitForTargetAndScroll();
  });

  // Smooth-scroll on hash deep link
  window.addEventListener('load', () => {
    if (location.hash && /review/i.test(location.hash)) {
      // e.g., #all-reviews or #reviews
      waitForTargetAndScroll();
    }
  });
})();